<script setup lang="ts">
import { computed, ref } from "vue";
import {
  UiTabs,
  UiTabsItem,
  UiIcon,
  UiButton,
  UiAside,
} from "@shopexperts/shared";
import PageHeader from "@/app/components/common/PageHeader.vue";
import LeadSubmissionForm from "@/app/components/LeadSubmissionForm.vue";
import ExpertProjectInviteForm from "@/app/components/ExpertProjectInviteForm.vue";
import ProjectListContent from "@/v2Projects/components/ProjectListContent.vue";
import { useProjectsList } from "@/v2Projects/composables/useProjectsList";

const {
  side,
  activeTab,
  clientGroups,
  expertProjects,
  totalCount,
  isLoading,
  error,
  refetch,
} = useProjectsList();

const pageTitle = computed(() =>
  side.value === "client" ? "My Projects" : "Leads & Projects",
);

const pageDescription = computed(() =>
  side.value === "client"
    ? "Manage all your active projects in one place.<br />Track progress and communicate with your matched experts."
    : "Track your leads and project assignments in one place.<br />Manage quotes, updates, and client communications.",
);

const isClient = computed(() => side.value === "client");
const defaultTab = computed(() => (isClient.value ? "all" : "leads"));

const asideOpen = ref(false);
const formKey = ref(0);

function openSubmitAside() {
  formKey.value++;
  asideOpen.value = true;
}

function handleSubmitSuccess() {
  asideOpen.value = false;
  refetch();
}

function handleExpertInviteSuccess() {
  refetch();
}
</script>

<template>
  <main class="flex-1 p-4 md:p-8 overflow-y-auto bg-secondary font-light w-full">
    <PageHeader :title="pageTitle" :description="pageDescription">
      <template #action>
        <UiButton
          :variant="isClient ? 'primary' : 'secondary'"
          :label="isClient ? 'Submit a Request' : 'Create a Project'"
          show-arrow
          @click="openSubmitAside"
        />
      </template>
    </PageHeader>

    <UiTabs v-model="activeTab" :default-value="defaultTab">
      <template v-if="isClient">
        <UiTabsItem
          value="all"
          label="All Projects"
          :count="activeTab === 'all' ? totalCount : undefined"
        >
          <ProjectListContent
            :side="side"
            :is-loading="isLoading"
            :error="(error as Error | null)"
            :client-groups="clientGroups"
            :expert-projects="expertProjects"
            :total-count="totalCount"
            @retry="refetch"
            @submit="openSubmitAside"
          />
        </UiTabsItem>

        <UiTabsItem
          value="active"
          label="Active Projects"
          :count="activeTab === 'active' ? totalCount : undefined"
        >
          <ProjectListContent
            :side="side"
            :is-loading="isLoading"
            :error="(error as Error | null)"
            :client-groups="clientGroups"
            :expert-projects="expertProjects"
            :total-count="totalCount"
            empty-title="No active projects"
            empty-description="Projects that are pending, quoted, in progress, or submitted will appear here."
            @retry="refetch"
            @submit="openSubmitAside"
          />
        </UiTabsItem>

        <UiTabsItem
          value="closed"
          label="Closed Projects"
          :count="activeTab === 'closed' ? totalCount : undefined"
        >
          <ProjectListContent
            :side="side"
            :is-loading="isLoading"
            :error="(error as Error | null)"
            :client-groups="clientGroups"
            :expert-projects="expertProjects"
            :total-count="totalCount"
            empty-title="No closed projects"
            empty-description="Completed, cancelled, or rejected projects will appear here."
            @retry="refetch"
            @submit="openSubmitAside"
          />
        </UiTabsItem>
      </template>

      <template v-else>
        <UiTabsItem
          value="leads"
          label="Leads"
          :count="activeTab === 'leads' ? totalCount : undefined"
        >
          <ProjectListContent
            :side="side"
            :is-loading="isLoading"
            :error="(error as Error | null)"
            :client-groups="clientGroups"
            :expert-projects="expertProjects"
            :total-count="totalCount"
            empty-title="No leads"
            empty-description="Requests that are pending quote will appear here."
            @retry="refetch"
            @submit="openSubmitAside"
          />
        </UiTabsItem>

        <UiTabsItem
          value="active"
          label="Active Projects"
          :count="activeTab === 'active' ? totalCount : undefined"
        >
          <ProjectListContent
            :side="side"
            :is-loading="isLoading"
            :error="(error as Error | null)"
            :client-groups="clientGroups"
            :expert-projects="expertProjects"
            :total-count="totalCount"
            empty-title="No active projects"
            empty-description="Projects that are quoted, in progress, or submitted will appear here."
            @retry="refetch"
            @submit="openSubmitAside"
          />
        </UiTabsItem>

        <UiTabsItem
          value="closed"
          label="Closed"
          :count="activeTab === 'closed' ? totalCount : undefined"
        >
          <ProjectListContent
            :side="side"
            :is-loading="isLoading"
            :error="(error as Error | null)"
            :client-groups="clientGroups"
            :expert-projects="expertProjects"
            :total-count="totalCount"
            empty-title="No closed projects"
            empty-description="Completed, cancelled, or rejected projects will appear here."
            @retry="refetch"
            @submit="openSubmitAside"
          />
        </UiTabsItem>

        <UiTabsItem
          value="all"
          label="All"
          :count="activeTab === 'all' ? totalCount : undefined"
        >
          <ProjectListContent
            :side="side"
            :is-loading="isLoading"
            :error="(error as Error | null)"
            :client-groups="clientGroups"
            :expert-projects="expertProjects"
            :total-count="totalCount"
            @retry="refetch"
            @submit="openSubmitAside"
          />
        </UiTabsItem>
      </template>
    </UiTabs>

    <UiAside
      v-model="asideOpen"
      position="right"
      :close-on-overlay-click="false"
    >
      <div class="p-6 md:p-8 flex flex-col gap-4 max-h-full overflow-y-auto">
        <template v-if="isClient">
          <LeadSubmissionForm
            :key="formKey"
            :source="'expert'"
            :disable-auto-redirect="true"
            @success="handleSubmitSuccess"
          >
            <template #close-button>
              <UiButton variant="ghost" shape="square" @click="asideOpen = false">
                <template #before_label>
                  <UiIcon name="close" class="w-6 h-6" />
                </template>
              </UiButton>
            </template>
          </LeadSubmissionForm>
        </template>

        <template v-else>
          <ExpertProjectInviteForm
            :key="formKey"
            @submitted="handleExpertInviteSuccess"
            @close="asideOpen = false"
          >
            <template #close-button>
              <UiButton variant="ghost" shape="square" @click="asideOpen = false">
                <template #before_label>
                  <UiIcon name="close" class="w-6 h-6" />
                </template>
              </UiButton>
            </template>
          </ExpertProjectInviteForm>
        </template>
      </div>
    </UiAside>
  </main>
</template>
